import React from "react";
import { Row, Col, Pagination, Empty, Spin, Select, theme } from "antd";
import { useTranslation } from "react-i18next";
import { IProduct } from "../../../models/product/response";
import ProductCard from "./ProductCard";

interface Props {
    products: IProduct[];
    loading: boolean;
    storeId?: number;
    selectedProductIds: number[];
    currentPage: number;
    perPage: number;
    total: number;
    onPageChange: (page: number, pageSize: number) => void;
    onPerPageChange: (perPage: number) => void;
    onProductAdd: (product: IProduct) => void;
    onProductEdit: (product: IProduct) => void;
}

const ProductGrid: React.FC<Props> = ({
    products,
    loading,
    storeId,
    selectedProductIds,
    currentPage,
    perPage,
    total,
    onPageChange,
    onPerPageChange,
    onProductAdd,
    onProductEdit,
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    const perPageOptions = [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
    ];

    if (loading) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
            }}>
                <Spin size="large" tip={t("loading_products")} />
            </div>
        );
    }

    if (!products || products.length === 0) {
        return (
            <div style={{
                padding: "40px 0",
                textAlign: "center",
            }}>
                <Empty
                    description={t("no_products_found")}
                    style={{ fontSize: 16 }}
                />
            </div>
        );
    }

    return (
        <div>
            {/* Products Grid */}
            <Row gutter={[16, 16]}>
                {products.map((product) => (
                    <Col
                        key={product.id}
                        xs={24}
                        sm={12}
                        md={8}
                        lg={6}
                        xl={6}
                    >
                        <ProductCard
                            product={product}
                            storeId={storeId}
                            isSelected={selectedProductIds.includes(product.id)}
                            onAdd={onProductAdd}
                            onEdit={onProductEdit}
                        />
                    </Col>
                ))}
            </Row>

            {/* Pagination Controls */}
            <div style={{
                marginTop: 32,
                padding: "20px 0",
                borderTop: `1px solid ${token.colorBorder}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
            }}>
                {/* Items per page selector */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                }}>
                    <span style={{ fontSize: 14, color: token.colorTextSecondary }}>
                        {t("items_per_page")}:
                    </span>
                    <Select
                        value={perPage}
                        onChange={onPerPageChange}
                        options={perPageOptions}
                        style={{ width: 100 }}
                    />
                </div>

                {/* Pagination */}
                <Pagination
                    current={currentPage}
                    pageSize={perPage}
                    total={total}
                    onChange={onPageChange}
                    showSizeChanger={false}
                    showTotal={(total, range) =>
                        `${range[0]}-${range[1]} ${t("of")} ${total} ${t("items")}`
                    }
                    style={{ flexShrink: 0 }}
                />
            </div>
        </div>
    );
};

export default ProductGrid;
